// src/adminDashboard/shared/components/DataTable/utils/exportHelpers.ts
// Export utilities for DataTable

import * as XLSX from 'xlsx';

type RowData = object;
type RowRecord = Record<string, unknown> & { id?: string | number };
type BivariantRowHandler<TResult> = {
    bivarianceHack(row: RowData): TResult;
}['bivarianceHack'];
type BivariantRowIndexHandler<TResult> = {
    bivarianceHack(row: RowData, index: number): TResult;
}['bivarianceHack'];

export interface ColumnDef {
    key: string;
    header: string;
    exportHeader?: string;
    exportable?: boolean;
    getValue?: BivariantRowHandler<unknown>;
    exportValue?: BivariantRowHandler<unknown>;
    render?: BivariantRowIndexHandler<React.ReactNode>;
    align?: 'left' | 'center' | 'right';
    width?: string | number;
    sortable?: boolean;
    toggleable?: boolean;
}

/**
 * Download a file with the given content
 */
const downloadFile = (content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = globalThis.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    globalThis.URL.revokeObjectURL(url);
};

const toRowRecord = (row: RowData): RowRecord => row as RowRecord;

/**
 * Get the value for a cell from a row and column definition
 */
const getCellValue = (row: RowData, column: ColumnDef): unknown => {
    if (column.exportValue) {
        return column.exportValue(row);
    }
    if (column.getValue) {
        return column.getValue(row);
    }
    return toRowRecord(row)[column.key];
};

/**
 * Format a value for export (handle dates, numbers, etc.)
 */
const formatExportValue = (value: unknown): string => {
    if (value === null || value === undefined) {
        return '';
    }
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }
    return String(value);
};

/**
 * Export data to CSV format
 */
export const exportToCSV = (data: RowData[], columns: ColumnDef[], filename = 'export') => {
    // Filter out columns that shouldn't be exported
    const exportableColumns = columns.filter(col => col.exportable !== false);

    // Create CSV header
    const headers = exportableColumns.map(col => col.exportHeader || col.header).join(',');

    // Create CSV rows
    const rows = data.map(row => {
        return exportableColumns
            .map(col => {
                const value = getCellValue(row, col);
                const formatted = formatExportValue(value);
                // Escape quotes and wrap in quotes if contains comma, quote, or newline
                if (formatted.includes(',') || formatted.includes('"') || formatted.includes('\n')) {
                    return `"${formatted.replace(/"/g, '""')}"`;
                }
                return formatted;
            })
            .join(',');
    });

    const csv = [headers, ...rows].join('\n');
    downloadFile(csv, `${filename}.csv`, 'text/csv;charset=utf-8;');
};

/**
 * Export data to Excel format
 */
export const exportToExcel = (data: RowData[], columns: ColumnDef[], filename = 'export') => {
    // Filter out columns that shouldn't be exported
    const exportableColumns = columns.filter(col => col.exportable !== false);

    // Create worksheet data
    const wsData = [
        // Header row
        exportableColumns.map(col => col.exportHeader || col.header),
        // Data rows
        ...data.map(row =>
            exportableColumns.map(col => {
                const value = getCellValue(row, col);
                return formatExportValue(value);
            })
        )
    ];

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Auto-size columns
    const colWidths = exportableColumns.map((col) => {
        const maxLength = Math.max(
            (col.exportHeader || col.header).length,
            ...data.map(row => {
                const value = getCellValue(row, col);
                return formatExportValue(value).length;
            })
        );
        return { wch: Math.min(maxLength + 2, 50) }; // Cap at 50 characters
    });
    ws['!cols'] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Data');

    // Generate Excel file and download
    XLSX.writeFile(wb, `${filename}.xlsx`);
};

/**
 * Export selected rows only
 */
export const exportSelected = (allData: RowData[], selectedIds: (string | number)[], columns: ColumnDef[], filename: string, format = 'csv') => {
    const selectedData = allData.filter((row) => {
        const rowId = toRowRecord(row).id;
        return rowId !== undefined && selectedIds.includes(rowId);
    });

    if (format === 'excel') {
        exportToExcel(selectedData, columns, filename);
    } else {
        exportToCSV(selectedData, columns, filename);
    }
};
